import { DataSource } from '~/Api/DataSource';
import { ApiClientError } from '~/ApiClient';
import Price from '~/utils/Price';

import { Error } from './Error';
import { mapError } from './mapError';

export interface GroupFinalCalculation {
	readonly baseReimbursement: BaseReimbursement | null;
	readonly emptyResultReason: Error[];
	readonly lowestPrices: LowestPrice[];
	readonly possibleSavings: PossibleSavings;
}

export interface BaseReimbursement {
	readonly averageOf2ndAnd3rdLowestExFactoryPricePerUTDD: Price | null;
	readonly deviationPercentage: number | null;
	readonly finalPrice: string | null;
	readonly lowestExFactoryPerUTDD: Price | null;
	readonly referenceProduct: ReferenceProduct | null;
}

export interface ReferenceProduct {
	readonly dataSource: DataSource | null;
	readonly exFactoryPrice: Price | null;
	readonly id: string | null;
	readonly packName: string | null;
	readonly productList: { readonly id: string } | null;
	readonly suklCode: string | null;
}

export interface LowestPrice {
	readonly dataSource: DataSource;
	readonly exFactoryPrice: Price;
	readonly id: string;
}

export interface PossibleSavings {
	readonly currentReimbursementPerUTDD: {
		readonly price: Price;
		readonly sameValueInAllLists: boolean;
	} | null;
	readonly possibleSavingsPercentage: number | null;
	readonly spendingPerGroup: {
		readonly price: Price;
		readonly year: number;
	} | null;
}

export interface RawGroupFinalCalculation {
	readonly baseReimbursement: BaseReimbursement | null;
	readonly emptyResultReason: ApiClientError[];
	readonly lowestPrices: LowestPrice[];
	readonly possibleSavings: RawPossibleSavings;
}

interface RawPossibleSavings {
	readonly currentReimbursementPerUTDD: {
		readonly currency: string;
		readonly value: string;
		readonly sameValueInAllLists: boolean;
	} | null;
	readonly possibleSavingsPercentage: number | null;
	readonly spendingPerGroup: {
		readonly currency: string;
		readonly value: string;
		readonly year: number;
	} | null;
}

const mapPrice = ({ value, currency }: { value: string; currency: string }): Price => new Price(value, currency);

export const mapGroupFinalCalculation = (finalCalculation: RawGroupFinalCalculation): GroupFinalCalculation => ({
	...finalCalculation,
	baseReimbursement:
		finalCalculation.baseReimbursement !== null
			? {
					...finalCalculation.baseReimbursement,
					averageOf2ndAnd3rdLowestExFactoryPricePerUTDD:
						finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD !== null
							? mapPrice(finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD)
							: null,
					lowestExFactoryPerUTDD:
						finalCalculation.baseReimbursement.lowestExFactoryPerUTDD !== null
							? mapPrice(finalCalculation.baseReimbursement.lowestExFactoryPerUTDD)
							: null,
					referenceProduct:
						finalCalculation.baseReimbursement.referenceProduct !== null
							? {
									...finalCalculation.baseReimbursement.referenceProduct,
									exFactoryPrice:
										finalCalculation.baseReimbursement.referenceProduct.exFactoryPrice !== null
											? mapPrice(finalCalculation.baseReimbursement.referenceProduct.exFactoryPrice)
											: null,
							  }
							: null,
			  }
			: null,
	emptyResultReason: finalCalculation.emptyResultReason.map(mapError),
	lowestPrices: finalCalculation.lowestPrices.map((lowestPrice) => ({
		...lowestPrice,
		exFactoryPrice: mapPrice(lowestPrice.exFactoryPrice),
	})),
	possibleSavings: {
		...finalCalculation.possibleSavings,
		currentReimbursementPerUTDD:
			finalCalculation.possibleSavings.currentReimbursementPerUTDD !== null
				? {
						sameValueInAllLists: finalCalculation.possibleSavings.currentReimbursementPerUTDD.sameValueInAllLists,
						price: mapPrice(finalCalculation.possibleSavings.currentReimbursementPerUTDD),
				  }
				: null,
		spendingPerGroup:
			finalCalculation.possibleSavings.spendingPerGroup !== null
				? {
						...finalCalculation.possibleSavings.spendingPerGroup,
						price: mapPrice(finalCalculation.possibleSavings.spendingPerGroup),
				  }
				: null,
	},
});
